import axios from "axios";

function isClientSideError(statusCode?: number) {
  if (!statusCode) return false;

  return statusCode >= 400 && statusCode < 500;
}

export function getFriendlyErrorMessage(error: unknown): string | undefined {
  if (axios.isAxiosError(error) && isClientSideError(error.response?.status)) {
    const message = error.response?.data?.message;

    return Array.isArray(message) ? message.join(" ") : message;
  }
  return;
}

function readBlobAsText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * A request made with `responseType: "blob"` receives its JSON error body as a Blob, so the message
 * getFriendlyErrorMessage looks for is unreachable until the body is read back and parsed.
 * @param {unknown} error - The rejection from a blob request
 * @returns {Promise<string | undefined>} The server's message, or undefined when there isn't a usable one
 */
export async function getFriendlyBlobErrorMessage(error: unknown): Promise<string | undefined> {
  const response = (error as { response?: { data?: unknown; status?: number } })?.response;

  if (!(response?.data instanceof Blob)) {
    return getFriendlyErrorMessage(error);
  }

  if (!isClientSideError(response.status)) {
    return undefined;
  }

  try {
    const message = JSON.parse(await readBlobAsText(response.data))?.message;

    return Array.isArray(message) ? message.join(" ") : message;
  } catch {
    return undefined;
  }
}
